import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Icon } from "../../kit";

/**
 * Small pieces shared by the CNC dialog and its profile editor, in the studio
 * look: white panels, ink text, hairline borders, ONE gold accent (`ed-accent`,
 * #946f2e) for what is chosen, amber for "read this", red for "this stops the
 * program". Type stays at 11px or above.
 */

export function CncSection({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5 border-t border-ed-border pt-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-display text-[12px] font-semibold text-ed-text">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function Banner({ tone, icon, children, testId }: { tone: "warn" | "fail" | "info"; icon: string; children: ReactNode; testId?: string }) {
  const cls = {
    warn: "border-amber-300 bg-amber-50 text-amber-900",
    fail: "border-red-300 bg-red-50 text-ed-red",
    info: "border-ed-border bg-ed-panel-2 text-ed-text-2",
  }[tone];
  return (
    <div data-testid={testId} className={`flex gap-2 rounded-lg border px-3 py-2.5 font-body text-[12px] leading-relaxed ${cls}`}>
      <Icon name={icon} size={16} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-ed-line/60 bg-ed-panel px-2.5 py-1.5 font-body text-[12px] text-ed-text outline-none transition-colors focus:border-ed-accent disabled:opacity-50";

export function LabeledText({
  label, value, onChange, placeholder, error, hint, testId, mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  error?: string | null;
  hint?: string;
  testId?: string;
  mono?: boolean;
}) {
  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="font-body text-[11px] font-medium text-ed-text-2">{label}</span>
      <input
        data-testid={testId}
        className={`${inputCls} ${mono ? "font-mono" : ""} ${error ? "border-ed-red" : ""}`}
        value={value}
        placeholder={placeholder}
        spellCheck={false}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
      />
      {error ? <span className="font-body text-[11px] text-ed-red">{error}</span> : hint ? <span className="font-body text-[11px] text-ed-text-3">{hint}</span> : null}
    </label>
  );
}

/**
 * A number box that commits on Enter or blur, never per keystroke (typing
 * "0.75" must not send 0 then 0.7). `onCommit` returns an error message to
 * refuse the value — the box then keeps what was typed and says why.
 */
export function NumInput({
  label, value, unit, onCommit, hint,
}: {
  label: string;
  value: number;
  unit?: string;
  onCommit: (v: number) => string | null;
  hint?: string;
}) {
  const [text, setText] = useState(String(value));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setText(String(value));
    setError(null);
  }, [value]);
  const commit = () => {
    const v = Number(text.trim());
    if (text.trim() === "" || !Number.isFinite(v)) return setError("Enter a number.");
    if (v === value) return setError(null);
    setError(onCommit(v));
  };
  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="font-body text-[11px] font-medium text-ed-text-2">{label}</span>
      <span className="flex items-center gap-1.5">
        <input
          className={`${inputCls} tabular-nums ${error ? "border-ed-red" : ""}`}
          inputMode="decimal"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
        />
        {unit ? <span className="shrink-0 font-body text-[11px] text-ed-text-3">{unit}</span> : null}
      </span>
      {error ? <span className="font-body text-[11px] text-ed-red">{error}</span> : hint ? <span className="font-body text-[11px] text-ed-text-3">{hint}</span> : null}
    </label>
  );
}

/** A row of mutually exclusive choices; the chosen one carries the gold accent. */
export function Choice<T extends string | number>({
  options, value, onChange, testId,
}: {
  options: readonly { id: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  testId?: string;
}) {
  return (
    <div data-testid={testId} className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button
            key={String(o.id)}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(o.id)}
            className={`rounded-md border px-3 py-1.5 font-body text-[12px] font-medium transition-colors ${
              on ? "border-ed-accent bg-ed-gold-tint text-ed-text" : "border-ed-border bg-ed-panel text-ed-text-2 hover:border-ed-line"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** A labelled on/off checkbox row. */
export function CheckRow({
  checked, onChange, label, hint, disabled, testId, id,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
  testId?: string;
  /** The checkbox's element id, for a check that points the user at it. */
  id?: string;
}) {
  return (
    <label className={`flex items-start gap-2.5 ${disabled ? "opacity-50" : "cursor-pointer"}`}>
      <input
        id={id}
        data-testid={testId}
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 accent-[#946f2e]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="flex flex-col">
        <span className="font-body text-[12px] text-ed-text">{label}</span>
        {hint ? <span className="font-body text-[11px] text-ed-text-3">{hint}</span> : null}
      </span>
    </label>
  );
}

/** How every disabled SmallButton looks, whatever its tone. */
export const SMALL_BUTTON_DISABLED = "cursor-not-allowed border-ed-border bg-transparent text-ed-text-3";

export function SmallButton({
  children, onClick, disabled, tone = "plain", testId, title,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: "plain" | "ink" | "danger";
  testId?: string;
  title?: string;
}) {
  // A disabled button drops its tone entirely — no fill, no hover, grey text
  // on a hairline — so a withheld download can never read as one to press (an
  // ink button merely faded to 40% looked like a grey, pressable one).
  const cls = disabled
    ? SMALL_BUTTON_DISABLED
    : {
      plain: "border-ed-border bg-ed-panel text-ed-text hover:border-ed-line",
      ink: "border-ed-text bg-ed-text text-white hover:bg-black",
      danger: "border-red-300 bg-ed-panel text-ed-red hover:border-ed-red",
    }[tone];
  return (
    <button
      type="button"
      data-testid={testId}
      title={title}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-1.5 font-body text-[12px] font-medium transition-colors ${cls}`}
    >
      {children}
    </button>
  );
}
